/**
 * Base custom element for every interactive piece of the theme.
 *
 * Deliberately tiny. There is no framework and no bundler here — the same shape
 * Shopify's own Horizon theme uses at 75 custom elements (see AGENTS.md) — so
 * this class exists only to remove the three things every component would
 * otherwise hand-roll:
 *
 *   1. `this.refs`      — child elements marked with `data-ref="name"`
 *   2. `this.on(...)`   — listeners that clean themselves up on disconnect
 *   3. `mount/unmount`  — clear lifecycle hooks instead of overriding callbacks
 *
 * Usage:
 *
 *   import { Component } from '@theme/component';
 *
 *   class AccountMenu extends Component {
 *     mount() {
 *       this.on(this.refs.toggle, 'click', () => this.open());
 *     }
 *   }
 *   customElements.define('account-menu', AccountMenu);
 *
 *   <account-menu>
 *     <button data-ref="toggle">Account</button>
 *   </account-menu>
 *
 * Progressive enhancement is the rule: the markup must be usable before this
 * class ever runs. Components enhance, they do not construct.
 */
export class Component extends HTMLElement {
  /**
   * Aborted on disconnect; every listener registered via `on()` is tied to it.
   * @type {AbortController | undefined}
   */
  #controller;

  /**
   * Named child elements, collected from `data-ref` attributes. A name used
   * more than once collects into an array.
   * @type {Record<string, HTMLElement | HTMLElement[]>}
   */
  refs = {};

  /**
   * Called once the element is connected and refs are collected.
   * Subclasses override this instead of `connectedCallback`.
   *
   * Declared as an empty METHOD, not an optional property. Declaring it as a
   * property makes it impossible for a subclass to define `mount() {}` the
   * natural way — TypeScript rejects overriding a property with a method
   * (TS2425). An empty method also removes the need for `this.mount?.()`.
   */
  mount() {}

  /** Called on disconnect, after listeners are torn down. */
  unmount() {}

  connectedCallback() {
    this.#controller = new AbortController();
    this.#collectRefs();
    this.mount();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.unmount();
  }

  /**
   * Collect `[data-ref]` descendants, skipping any that belong to a nested
   * component — those are that component's refs, not ours.
   */
  #collectRefs() {
    this.refs = {};
    const found = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-ref]'));
    for (const el of found) {
      if (el.closest(this.localName) !== this) continue;
      const name = el.dataset.ref;
      if (!name) continue;
      const existing = this.refs[name];
      if (existing === undefined) {
        this.refs[name] = el;
      } else if (Array.isArray(existing)) {
        existing.push(el);
      } else {
        this.refs[name] = [existing, el];
      }
    }
  }

  /**
   * Add a listener that is removed automatically when this element disconnects.
   * @param {EventTarget} target
   * @param {string} type
   * @param {EventListenerOrEventListenerObject} handler
   * @param {AddEventListenerOptions} [options]
   */
  on(target, type, handler, options = {}) {
    target.addEventListener(type, handler, { ...options, signal: this.#controller?.signal });
  }

  /**
   * Dispatch a composed, bubbling custom event from this element.
   * @param {string} type
   * @param {unknown} [detail]
   */
  emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }
}
